import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  FilamentQuery,
  CreateFilamentInput,
  UpdateFilamentInput,
  AddFilamentToLibraryInput,
  UpdateTenantFilamentInput,
} from '@maker-toolchest/shared';
import {
  filamentLibraryApi,
  tenantFilamentApi,
  lookupApi,
  type TenantFilamentQueryParams,
  type FilamentLibraryListResponse,
  type TenantFilamentListResponse,
} from './api';

// ============================================================================
// FILAMENT LIBRARY HOOKS
// ============================================================================

const LIBRARY_QUERY_KEY = 'filament-library';
const TENANT_FILAMENTS_KEY = 'tenant-filaments';
const LOOKUP_KEY = 'filament-lookups';

/**
 * Search the filament library
 */
export function useFilamentLibrary(
  params?: Partial<FilamentQuery>,
  options?: Omit<UseQueryOptions<FilamentLibraryListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [LIBRARY_QUERY_KEY, params],
    queryFn: () => filamentLibraryApi.search(params),
    select: (response) => ({
      items: response.items,
      pagination: response.pagination,
    }),
    ...options,
  });
}

/**
 * Get a single filament from the library
 */
export function useFilament(id: string) {
  return useQuery({
    queryKey: [LIBRARY_QUERY_KEY, id],
    queryFn: () => filamentLibraryApi.get(id),
    enabled: !!id,
  });
}

/**
 * Create a custom filament
 */
export function useCreateFilament() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFilamentInput) => filamentLibraryApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
    },
  });
}

/**
 * Update a custom filament
 */
export function useUpdateFilament() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFilamentInput }) =>
      filamentLibraryApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
    },
  });
}

/**
 * Delete a custom filament
 */
export function useDeleteFilament() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => filamentLibraryApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIBRARY_QUERY_KEY] });
    },
  });
}

// ============================================================================
// TENANT FILAMENT HOOKS (My Library)
// ============================================================================

/**
 * List filaments in tenant's library
 */
export function useTenantFilaments(
  params?: TenantFilamentQueryParams,
  options?: Omit<UseQueryOptions<TenantFilamentListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [TENANT_FILAMENTS_KEY, params],
    queryFn: () => tenantFilamentApi.list(params),
    select: (response) => ({
      items: response.items,
      pagination: response.pagination,
    }),
    ...options,
  });
}

/**
 * Get a single tenant filament
 */
export function useTenantFilament(id: string) {
  return useQuery({
    queryKey: [TENANT_FILAMENTS_KEY, id],
    queryFn: () => tenantFilamentApi.get(id),
    enabled: !!id,
  });
}

/**
 * Add a filament to tenant's library
 */
export function useAddFilamentToLibrary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddFilamentToLibraryInput) => tenantFilamentApi.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_FILAMENTS_KEY] });
    },
  });
}

/**
 * Update tenant-specific notes
 */
export function useUpdateTenantFilament() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantFilamentInput }) =>
      tenantFilamentApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_FILAMENTS_KEY] });
    },
  });
}

/**
 * Remove filament from tenant's library
 */
export function useRemoveFilamentFromLibrary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantFilamentApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_FILAMENTS_KEY] });
    },
  });
}

// ============================================================================
// LOOKUP TABLE HOOKS
// ============================================================================

/**
 * Get all filament types
 */
export function useFilamentTypes() {
  return useQuery({
    queryKey: [LOOKUP_KEY, 'types'],
    queryFn: () => lookupApi.getTypes(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Get all filament manufacturers
 */
export function useFilamentManufacturers() {
  return useQuery({
    queryKey: [LOOKUP_KEY, 'manufacturers'],
    queryFn: () => lookupApi.getManufacturers(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all filament finishes
 */
export function useFilamentFinishes() {
  return useQuery({
    queryKey: [LOOKUP_KEY, 'finishes'],
    queryFn: () => lookupApi.getFinishes(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all embedded materials
 */
export function useEmbeddedMaterials() {
  return useQuery({
    queryKey: [LOOKUP_KEY, 'embeddedMaterials'],
    queryFn: () => lookupApi.getEmbeddedMaterials(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all lookup data at once
 */
export function useFilamentLookups() {
  const types = useFilamentTypes();
  const manufacturers = useFilamentManufacturers();
  const finishes = useFilamentFinishes();
  const embeddedMaterials = useEmbeddedMaterials();

  return {
    types: types.data || [],
    manufacturers: manufacturers.data || [],
    finishes: finishes.data || [],
    embeddedMaterials: embeddedMaterials.data || [],
    isLoading: types.isLoading || manufacturers.isLoading || finishes.isLoading || embeddedMaterials.isLoading,
  };
}

/**
 * Create a new filament type
 */
export function useCreateFilamentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => lookupApi.createType(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOOKUP_KEY, 'types'] });
    },
  });
}

/**
 * Create a new filament manufacturer
 */
export function useCreateFilamentManufacturer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => lookupApi.createManufacturer(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOOKUP_KEY, 'manufacturers'] });
    },
  });
}

/**
 * Create a new filament finish
 */
export function useCreateFilamentFinish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => lookupApi.createFinish(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOOKUP_KEY, 'finishes'] });
    },
  });
}

/**
 * Create a new embedded material
 */
export function useCreateEmbeddedMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => lookupApi.createEmbeddedMaterial(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOOKUP_KEY, 'embeddedMaterials'] });
    },
  });
}
